import { InteractionLogService } from './interaction-log.service';

/**
 * PerformanceLog is a method decorator factory
 * It returns a method decorator
 *
 * The returned decorator will wrap the original method
 * and log initial timestamp, final timestamp and execution duration of the method
 *
 * It will also consider if the returned value is a Promise
 * and register for the Promise resolution
 *
 * It does not handle erros or promises rejected
 */
export function PerformanceLog(
  logPrefix = 'PerformanceLog',
  accountForRendering = false,
  countExecution = false,
  name?: string,
  data?: () => any
): MethodDecorator {
  return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
    const targetName = target?.constructor?.name || '';

    if (!descriptor || !(descriptor.value instanceof Function)) {
      throw new TypeError(`${logPrefix} is a method decorator`);
    }

    const method = descriptor.value;
    let count = 0;

    descriptor.value = function (...args: any[]) {
      count++;
      const keyName = name || `${targetName}.${propertyKey}`;
      const key = `${logPrefix}: ${keyName}` + ((countExecution && ` (${count})`) || '');

      // log init
      const start = Date.now();
      const instance = InteractionLogService.instance();
      const sData = (data && data.call(this)) || '';
      const startData = sData ? `${start} - ${sData}` : start;
      instance && instance.performanceLog(key, 'start', startData);

      // call real method
      let result: Promise<any> | any;

      try {
        result = method.call(this, ...args);
      } catch (error) {
        instance && instance.performanceLog(key, 'error', error);
        throw error;
      }

      if (result instanceof Promise) {
        // if promise, return it and register a then block to get the end of execution
        return new Promise<any>((resolve, reject) => {
          result
            .then(value => {
              // log end
              logEnd(key, start, accountForRendering);
              resolve(value);
            })
            .catch(error => {
              instance && instance.performanceLog(key, 'error', error);
              reject(error);
            });
        });
      }

      // log end
      logEnd(key, start, accountForRendering);
      return result;
    };
  };
}

function logEnd(name: string, start: number, accountForRendering) {
  const end = Date.now();
  const instance = InteractionLogService.instance();

  if (accountForRendering) {
    instance && instance.performanceLog(name, 'end execution', end);
    instance && instance.performanceLog(name, 'total execution', end - start);
    window.setTimeout(() => {
      const endRendering = Date.now();
      instance && instance.performanceLog(name, 'end rendering', endRendering);
      instance && instance.performanceLog(name, 'total rendering', endRendering - end);
      instance && instance.performanceLog(name, 'total', endRendering - start);
    }, 0);
  } else {
    instance && instance.performanceLog(name, 'end', end);
    instance && instance.performanceLog(name, 'total', end - start);
  }
}
